/**
 * Cash rounding and split tender. Appendix D `MR-03` steps 11 and 12, `MR-06`, `MR-08`;
 * §6.7 `POS-010a`; §13 `PK-030`.
 */
export class TenderError extends Error {
}
export function roundCashTender(amountMinor, rounding) {
    const step = rounding.smallestDenominationMinor;
    if (step <= 0n) {
        throw new TenderError('The smallest circulating denomination must be a positive number of minor units');
    }
    if (step === 1n) {
        // The minor unit itself circulates, so there is nothing to round and no difference to post.
        return { payableMinor: amountMinor, roundingDifferenceMinor: 0n };
    }
    const negative = amountMinor < 0n;
    const magnitude = negative ? -amountMinor : amountMinor;
    const remainder = magnitude % step;
    let roundedMagnitude;
    if (remainder === 0n) {
        roundedMagnitude = magnitude;
    }
    else {
        const down = magnitude - remainder;
        const up = down + step;
        const twice = remainder * 2n;
        if (twice > step)
            roundedMagnitude = up;
        else if (twice < step)
            roundedMagnitude = down;
        else
            roundedMagnitude = rounding.halfGoes === 'up' ? up : down;
    }
    const payableMinor = negative ? -roundedMagnitude : roundedMagnitude;
    return { payableMinor, roundingDifferenceMinor: payableMinor - amountMinor };
}
export function allocateTenders(orderTotalMinor, tenders) {
    if (tenders.length === 0) {
        return { allocations: [], outstandingMinor: orderTotalMinor, changeMinor: 0n };
    }
    const allocations = [];
    let remaining = orderTotalMinor;
    tenders.forEach((tender, index) => {
        const isLast = index === tenders.length - 1;
        // Every tender before the last is capped at what is still owed, so a customer handing over a
        // 5,000 note against a 1,200 balance does not "pay" 5,000 toward the order.
        const applied = isLast ? tender.amountMinor : min(tender.amountMinor, remaining);
        remaining -= applied;
        allocations.push({
            tenderId: tender.id,
            appliedMinor: applied,
            remainingMinor: remaining > 0n ? remaining : 0n,
        });
    });
    // The residual lands on the last tender, which is MR-08's rule and also how a drawer works: change
    // comes out of the payment that finished the sale.
    return {
        allocations,
        outstandingMinor: remaining > 0n ? remaining : 0n,
        changeMinor: remaining < 0n ? -remaining : 0n,
    };
}
function min(a, b) {
    return a < b ? a : b;
}
/** The property that makes a drawer balance: what was applied equals what was tendered. */
export function tendersBalance(result, tenders) {
    const applied = result.allocations.reduce((sum, a) => sum + a.appliedMinor, 0n);
    const tendered = tenders.reduce((sum, t) => sum + t.amountMinor, 0n);
    return applied === tendered;
}
//# sourceMappingURL=tender.js.map